import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import useIframeEmbed from '../../../utils/useIframeEmbed';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;

  ${({ $fillHeight }) =>
    $fillHeight &&
    css`
      /* Fill the flex column MasterLayout provides instead of guessing a
         viewport offset. min-height: 0 lets it shrink to fit. */
      flex: 1;
      min-height: 0;
    `}
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  font-size: 16px;
  color: #666;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 400px;
  padding: 20px;
  text-align: center;
`;

const ErrorTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #d32f2f;
  margin-bottom: 8px;
`;

const ErrorMessage = styled.div`
  font-size: 14px;
  color: #666;
`;

const IframeContainer = styled.div`
  flex: 1;
  width: 100%;
  ${({ $fillHeight }) =>
    $fillHeight &&
    css`
      min-height: 0;
    `}

  iframe {
    overflow: hidden;
    background-color: transparent;
    width: 100%;
    /* Fixed fallback kept for tab-panel embeds with no definite height. It
       assumes ~200px of chrome, so it overflows any screen with a PageBar. */
    height: ${({ $fillHeight }) =>
      $fillHeight ? '100%' : 'calc(100vh - 200px)'};
    border: 0;
    display: block; /* avoids the inline line-box gap at exactly 100% */
  }
`;

/**
 * Generic iframe embed component for QCP tools
 *
 * Handles token generation, loading states, error states, and iframe rendering
 * for all QCP embedded tools (medication conflict, benchmarking, knowledge hub, bundles, etc.)
 *
 * @param {Object} props - Component props
 * @param {Object} props.apiSpec - The API spec to call for token generation
 * @param {Object} props.params - Parameters to pass to the API (e.g., recipientId, projectId, participationId)
 * @param {string} props.title - Iframe title attribute for accessibility
 * @param {string} props.toolName - Display name used in loading/error messages
 *
 * @example
 * // For features that don't need parameters
 * <IframeEmbed
 *   apiSpec={apiZedocGetMedicationConflictToken}
 *   params={{}}
 *   title="Medication Conflict Checker"
 *   toolName="medication conflict tool"
 * />
 *
 * @example
 * // For Patient Bundle (needs recipientId)
 * <IframeEmbed
 *   apiSpec={apiZedocGetPatientBundleToken}
 *   params={{ recipientId: 'xyz123' }}
 *   title="Patient Bundle"
 *   toolName="patient bundle tool"
 * />
 */
const IframeEmbed = ({ apiSpec, params = {}, title, toolName, fillHeight }) => {
  const iframeRef = useRef(null);
  const containerRef = useRef(null);
  const { embedUrl, sessionId, loading, error } = useIframeEmbed({
    apiSpec,
    params,
  });

  // PostMessage communication to send sessionId securely to iframe
  useEffect(() => {
    if (!embedUrl || !sessionId) return;

    // A frontendUrl configured without a scheme or host makes this throw, and
    // an exception raised here unmounts the tree to a blank screen. Skip the
    // handshake instead: the iframe still renders, as it did before this
    // effect existed.
    let embedOrigin;
    try {
      embedOrigin = new URL(embedUrl).origin;
    } catch {
      return;
    }

    const handleMessage = (event) => {
      // The session id is deliberately kept out of the embed URL, so it must
      // only ever reach the frame we opened. Without both checks any window
      // that can postMessage into this one could ask for it and be answered.
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (event.origin !== embedOrigin) return;

      // Handle "iframe_ready" message from child iframe
      if (event.data?.type === 'iframe_ready' && event.data.nonce) {
        // Send sessionId securely via postMessage
        iframeRef.current?.contentWindow.postMessage(
          {
            type: 'session_credentials',
            sessionId: sessionId,
            nonce: event.data.nonce,
          },
          embedOrigin,
        );
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [embedUrl, sessionId]);

  // Simple right-click prevention (production only)
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;

    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('contextmenu', handleContextMenu);
      return () =>
        container.removeEventListener('contextmenu', handleContextMenu);
    }
  }, []);

  if (loading) {
    return (
      <Container $fillHeight={fillHeight}>
        <LoadingContainer>Loading {toolName}...</LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container $fillHeight={fillHeight}>
        <ErrorContainer>
          <ErrorTitle>Unable to load {toolName}</ErrorTitle>
          <ErrorMessage>{error}</ErrorMessage>
        </ErrorContainer>
      </Container>
    );
  }

  return (
    <Container $fillHeight={fillHeight}>
      <IframeContainer
        $fillHeight={fillHeight}
        ref={containerRef}
        style={{ userSelect: 'none' }}
        onContextMenu={(e) => {
          if (process.env.NODE_ENV === 'production') {
            e.preventDefault();
          }
        }}
      >
        <iframe
          ref={iframeRef}
          src={embedUrl}
          title={title}
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </IframeContainer>
    </Container>
  );
};

IframeEmbed.propTypes = {
  apiSpec: PropTypes.shape({
    withParams: PropTypes.func.isRequired,
  }).isRequired,
  params: PropTypes.object,
  title: PropTypes.string.isRequired,
  toolName: PropTypes.string.isRequired,
  // Fill the parent flex column instead of the fixed calc(100vh - 200px).
  // For full-page screens with a PageBar; leave off inside tab panels.
  fillHeight: PropTypes.bool,
};

IframeEmbed.defaultProps = {
  params: {},
  fillHeight: false,
};

export default IframeEmbed;
